import { ReactElement, useMemo } from 'react';
import { HiOutlinePencilSquare } from 'react-icons/hi2';
import Button from '../../components/Button';
import { useNavigate } from 'react-router-dom';

type ColumnDef = {
  label: string;
  field: string;
  precision: number;
};

// Core parameters that should always be displayed (exactly 6 columns)
const CORE_COLUMNS: ColumnDef[] = [
  { label: 'Mass', field: 'combined_mass', precision: 1 },
  { label: 'Crr', field: 'crr', precision: 5 },
  { label: 'Cp Corr', field: 'cp_corr', precision: 3 },
  { label: 'Wheel Circumference', field: 'wheel_circumference', precision: 3 },
  { label: 'Yaw Offset', field: 'yaw_offset', precision: 1 },
  { label: 'Elevation Difference', field: 'elevation_difference', precision: 1 },
];

// Additional columns that may be shown for branch configs
const ADDITIONAL_COLUMNS: ColumnDef[] = [
  { label: 'Power Slope', field: 'power_meter_slope', precision: 3 },
  { label: 'CDA Ref', field: 'cda_ref', precision: 3 },
  { label: 'Speed Ref', field: 'airspeed_ref', precision: 1 },
  { label: 'Speed Slope', field: 'airspeed_slope', precision: 5 },
  { label: 'Crosswind Slope', field: 'xw_para_slope', precision: 7 },
  { label: 'Chest Ref', field: 'chest_ref', precision: 1 },
  { label: 'Head Ref', field: 'head_ref', precision: 1 },
  { label: 'Chest Slope', field: 'chest_slope', precision: 5 },
  { label: 'Head Slope', field: 'head_slope', precision: 5 },
  { label: 'XW Wheel Compensation', field: 'xw_wheel_compensation', precision: 3 },
];

export default function CalibrationListGrid(props: {
  calibrations: any[];
  sessionId?: string;
  branchParameters?: string[];
}): ReactElement {
  const { calibrations, sessionId, branchParameters = [] } = props;
  const navigate = useNavigate();

  const runRowStyle = 'cr-border-solid cr-border-gray-300 cr-border-b-2';
  const editButtonStyle =
    'cr-border-none cr-rounded-full cr-text-[#03365D] !cr-bg-gray-100 hover:cr-bg-gray-200 cr-transition-colors cr-duration-200';

  // Determine which columns to show based on calibration data
  const columnsToShow = useMemo(() => {
    // Always include core columns that have data
    const coreColumnsWithData = CORE_COLUMNS.filter((col) =>
      calibrations.some(
        (calibration) => Object.prototype.hasOwnProperty.call(calibration, col.field) && calibration[col.field] != null,
      ),
    );

    // Only add additional columns if they are branch parameters
    const additionalColumnsToShow = ADDITIONAL_COLUMNS.filter((col) => {
      // Check if this parameter is in the branch parameters list
      if (!branchParameters.includes(col.field)) return false;

      // Also check if the data exists in at least one calibration
      return calibrations.some(
        (calibration) => Object.prototype.hasOwnProperty.call(calibration, col.field) && calibration[col.field] != null,
      );
    });

    return [...coreColumnsWithData, ...additionalColumnsToShow];
  }, [calibrations, branchParameters]);

  // Helper function to get total number of visible columns (including calibration and edit button)
  const getVisibleColumnCount = () => {
    return columnsToShow.length + 2; // +2 for calibration and edit button
  };

  // Helper function to generate grid template columns based on selected columns
  const getGridTemplateColumns = () => {
    const length = columnsToShow.length;
    const gridColumns = ['0.2fr', '0.5fr']; // Edit button and Calibration columns
    columnsToShow.forEach(() => {
      gridColumns.push(`${6 / length}fr`);
    });
    return gridColumns.join(' ');
  };

  // Helper function to format calibration value
  const formatValue = (calibration: any, field: string, precision: number) => {
    const value = calibration[field];
    return value != null ? value.toFixed(precision) : '-';
  };

  const handleEditClick = (calibrationId: string) => {
    if (sessionId) {
      navigate(`/sessions/${sessionId}/calibrations/${calibrationId}`);
    } else {
      navigate(`/calibrations/${calibrationId}`);
    }
  };

  return (
    <div className="cr-w-full cr-max-w-[100vw] cr-overflow-x-auto">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: getGridTemplateColumns(),
          width: '100%',
          minWidth: 'min-content',
        }}
        className="cr-bg-gray-50 cr-px-6 cr-pb-6 cr-text-center"
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: `span ${getVisibleColumnCount()}`,
          }}
          className="cr-font-secondary cr-text-[16px] cr-uppercase"
        >
          <div className={`${runRowStyle}`}></div>
          <div
            className={`${runRowStyle} cr-flex cr-items-center cr-justify-center cr-truncate 
                        cr-text-[10px] md:cr-text-[14px]`}
          >
            Config
          </div>
          {columnsToShow.map((col) => (
            <div
              key={col.field}
              className={`${runRowStyle} cr-flex cr-h-[100px] 
                            cr-items-center cr-justify-center md:cr-h-auto md:cr-p-2`}
            >
              <div className="cr-flex cr-h-[100px] cr-w-[10px] md:cr-h-auto md:cr-w-auto">
                <div
                  className="cr-rotate-90 cr-whitespace-nowrap cr-text-[10px] md:cr-rotate-0 md:cr-transform 
              md:cr-whitespace-normal md:cr-text-[14px]"
                >
                  {col.label}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: `span ${getVisibleColumnCount()}`,
          }}
        >
          {calibrations &&
            calibrations.map((calibration: any) => (
              <div
                key={calibration.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'subgrid',
                  gridColumn: `span ${getVisibleColumnCount()}`,
                }}
                className="cr-font-main"
              >
                <div className={`${runRowStyle} cr-flex cr-items-center cr-justify-center`}>
                  <Button className={editButtonStyle} onClick={() => handleEditClick(calibration.id)}>
                    <HiOutlinePencilSquare className="cr-size-5" />
                  </Button>
                </div>
                <div className={`${runRowStyle} cr-flex cr-items-center cr-justify-center
                  cr-break-word cr-text-[10px] md:cr-text-[14px]`}>
                  {(() => {
                    const definingConfig = calibration.letter;
                    if (!definingConfig) {
                      return <span className="cr-text-gray-500">{calibration.description}</span>;
                    }
                    return (
                      <span
                        className="cr-rounded cr-border-2 cr-border-solid cr-border-gray-400 cr-bg-gray-100 cr-px-1.5 cr-py-0.5 cr-text-xs cr-font-bold cr-text-[#03365D]"
                        title={`Defining Config ${definingConfig}`}
                      >
                        {definingConfig}
                      </span>
                    );
                  })()}
                </div>
                {columnsToShow.map((col) => (
                  <div key={col.field} className={`${runRowStyle} cr-flex cr-items-center cr-justify-center
                    cr-truncate cr-text-[10px] md:cr-text-[14px]`}>
                    {Object.prototype.hasOwnProperty.call(calibration, col.field)
                      ? formatValue(calibration, col.field, col.precision)
                      : '-'}
                  </div>
                ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
